import { Game, PracticeShot, ShotType, Turn } from '../types/curling';
import { fitSmoothCurve } from './curveFit';

export type QuadrantSide = 'left' | 'right';
export type QuadrantDirection = 'left-right' | 'right-left'; // clockwise (in-turn) | counter-clockwise (out-turn)
export type QuadrantEndType = 'away' | 'home';

/** A specific broom-side / turn-direction / end-parity combination on the sheet. */
export interface QuadrantKey {
  endType: QuadrantEndType;
  side: QuadrantSide;
  direction: QuadrantDirection;
}

export type TimeEstimateSource = 'recent' | 'average' | 'mirror' | 'baseline';

export interface QuadrantTimeEstimate {
  time: number;
  source: TimeEstimateSource;
  /** False only for 'recent'/'average' — a real reading of this exact quadrant. */
  isEstimate: boolean;
  /** How many of this quadrant's own shots the figure is built from (0 for 'mirror'/'baseline'). */
  shotsUsed: number;
}

const RECENT_SHOT_COUNT = 3;

export const turnToDirection = (turn: Turn): QuadrantDirection =>
  turn === 'clockwise' ? 'left-right' : 'right-left';

export const sideFromBroomPosition = (broomPosition: number): QuadrantSide =>
  broomPosition >= 0 ? 'right' : 'left';

export const endTypeFromEndNumber = (endNumber: number): QuadrantEndType =>
  endNumber % 2 !== 0 ? 'away' : 'home';

const quadrantKeyString = (q: QuadrantKey) => `${q.endType}-${q.side}-${q.direction}`;

/** The mirror quadrant: same trajectory (toward center or toward the edge), approached from the other side. */
export const oppositeQuadrant = (q: QuadrantKey): QuadrantKey => ({
  endType: q.endType,
  side: q.side === 'left' ? 'right' : 'left',
  direction: q.direction === 'left-right' ? 'right-left' : 'left-right',
});

/** Timestamped draw-to-tee-line times (zones 6-8 only), grouped by quadrant. */
export const collectQuadrantTimes = (game: Game): Map<string, { value: number; timestamp: number }[]> => {
  const acc = new Map<string, { value: number; timestamp: number }[]>();

  game.ends.forEach(end => {
    const endType = endTypeFromEndNumber(end.number);
    end.shots.forEach(shot => {
      if (shot.skipped || shot.time <= 0) return;
      if (shot.actualPosition < 6 || shot.actualPosition > 8) return;

      const key = quadrantKeyString({
        endType,
        side: sideFromBroomPosition(shot.broomPosition),
        direction: turnToDirection(shot.turn),
      });
      if (!acc.has(key)) acc.set(key, []);
      acc.get(key)!.push({ value: shot.time, timestamp: shot.timestamp });
    });
  });

  return acc;
};

const average = (entries: { value: number; timestamp: number }[]): { avg: number | null; used: number } => {
  if (entries.length === 0) return { avg: null, used: 0 };
  return { avg: entries.reduce((sum, e) => sum + e.value, 0) / entries.length, used: entries.length };
};

const recentAverage = (
  entries: { value: number; timestamp: number }[],
  count: number
): { avg: number | null; used: number } => {
  if (entries.length === 0) return { avg: null, used: 0 };
  const recent = [...entries].sort((a, b) => a.timestamp - b.timestamp).slice(-count);
  return average(recent);
};

const earliest = (entries: { value: number; timestamp: number }[]): number | null => {
  if (entries.length === 0) return null;
  return entries.reduce((first, e) => (e.timestamp < first.timestamp ? e : first)).value;
};

/** Average tee-line (zone 7) time from warm-up practice shots. */
const practiceTeeLineAverage = (practiceShots: PracticeShot[]): number | null => {
  const times = practiceShots
    .filter(p => p.time > 0 && /^7\./.test(p.zone))
    .map(p => p.time);
  if (times.length === 0) return null;
  return times.reduce((sum, t) => sum + t, 0) / times.length;
};

/**
 * Estimates the draw-to-tee-line time for one quadrant (broom side + turn
 * direction + which end), using the best data available. Shared by the Sheet
 * Visualizer (viewing curl/weight after the fact, or live mid-game) and the
 * live shot entry screen (estimating the next shot) so both draw the same
 * conclusion from the same data:
 *
 *  1. In a live game: the last few shots in this exact quadrant — a
 *     game-long average would hide how the ice has changed. Once the game is
 *     complete: every shot in this exact quadrant.
 *  2. No shots in this quadrant yet: the first shot from its mirror quadrant
 *     (same trajectory — toward center or toward the edge — just approached
 *     from the other side of the center line). First-rock times track
 *     closely across paths before wear differentiates them.
 *  3. Still nothing: the tee-line time from warm-up practice shots, or the
 *     time entered when the game was set up, in that order.
 *
 * Tiers 2 and 3 are flagged with isEstimate — a guess standing in for a
 * measurement of this quadrant, not a learned value for it.
 */
export const estimateQuadrantTime = (
  game: Game,
  practiceShots: PracticeShot[],
  quadrant: QuadrantKey,
  isLive: boolean
): QuadrantTimeEstimate => {
  const byQuadrant = collectQuadrantTimes(game);

  const ownEntries = byQuadrant.get(quadrantKeyString(quadrant)) ?? [];
  const own = isLive ? recentAverage(ownEntries, RECENT_SHOT_COUNT) : average(ownEntries);
  if (own.avg !== null) {
    return { time: own.avg, source: isLive ? 'recent' : 'average', isEstimate: false, shotsUsed: own.used };
  }

  const mirrorEntries = byQuadrant.get(quadrantKeyString(oppositeQuadrant(quadrant))) ?? [];
  const mirrorFirst = earliest(mirrorEntries);
  if (mirrorFirst !== null) {
    return { time: mirrorFirst, source: 'mirror', isEstimate: true, shotsUsed: 0 };
  }

  const practiceAvg = practiceTeeLineAverage(practiceShots);
  if (practiceAvg !== null) {
    return { time: practiceAvg, source: 'baseline', isEstimate: true, shotsUsed: 0 };
  }

  return { time: game.expectedTimes?.teeLine ?? 14.5, source: 'baseline', isEstimate: true, shotsUsed: 0 };
};

export interface QuadrantCurlEstimate {
  curl: number;
  source: TimeEstimateSource;
  isEstimate: boolean;
  shotsUsed: number;
}

/** Average curl (zone 7) from warm-up practice shots. */
const practiceTeeLineCurlAverage = (practiceShots: PracticeShot[]): number | null => {
  const curls = practiceShots.filter(p => /^7\./.test(p.zone)).map(p => p.curl);
  if (curls.length === 0) return null;
  return curls.reduce((sum, c) => sum + c, 0) / curls.length;
};

/**
 * Midpoint of the ~4.5-5ft ideal draw-weight curl range — the last-resort
 * default when nothing else is available: no shots in this game, no mirror
 * side, no practice shots, and no expectedCurl entered at game setup either
 * (see estimateBinnedCurl, which prefers game.expectedCurl over this).
 * Exported so the live shot entry screen's own expected-curl state starts
 * here too for a game with no setup value, rather than at a bare 0 that
 * neither represents a real estimate nor matches the Shot Visualizer.
 */
export const DEFAULT_CURL_FEET = 4.75;

const BROOM_BIN_WIDTH_FEET = 1;
const BROOM_POSITION_MIN = -6;
const BROOM_POSITION_MAX = 6;

/** Buckets a broom position (feet from center, -6 to +6) into a 1ft-wide bin, identified by its lower edge. */
const broomPositionBin = (broomPosition: number): number => {
  const clamped = Math.min(Math.max(broomPosition, BROOM_POSITION_MIN), BROOM_POSITION_MAX - 0.001);
  return Math.floor(clamped / BROOM_BIN_WIDTH_FEET) * BROOM_BIN_WIDTH_FEET;
};

interface BroomBinKey {
  endType: QuadrantEndType;
  bin: number;
  direction: QuadrantDirection;
}

const binKeyString = (k: BroomBinKey) => `${k.endType}-${k.bin}-${k.direction}`;

/**
 * Curl grouped by 1ft-wide broom-position bin instead of just a side of the
 * sheet — finer than the quadrant the Sheet Visualizer's summary table uses,
 * for the Shot Visualizer's continuously-draggable broom. shotTypeFilter
 * restricts to shots of exactly that shot type thrown at the called weight
 * (takeout weights, where curl doesn't scale from draw data); omit it to
 * count every shot regardless of type, same as draw-zone curl always has.
 */
const collectBinnedCurls = (
  game: Game,
  shotTypeFilter?: ShotType
): Map<string, { value: number; timestamp: number }[]> => {
  const acc = new Map<string, { value: number; timestamp: number }[]>();

  game.ends.forEach(end => {
    const endType = endTypeFromEndNumber(end.number);
    end.shots.forEach(shot => {
      if (shot.skipped) return;
      if (shotTypeFilter && (shot.shotType !== shotTypeFilter || shot.weight !== 'good')) return;

      const key = binKeyString({
        endType,
        bin: broomPositionBin(shot.broomPosition),
        direction: turnToDirection(shot.turn),
      });
      if (!acc.has(key)) acc.set(key, []);
      acc.get(key)!.push({ value: shot.curl, timestamp: shot.timestamp });
    });
  });

  return acc;
};

/**
 * Every populated bin's own average (or recent average, live) for one
 * endType + direction, as spline input points centered on each bin — the
 * aggregation step that keeps a handful of noisy individual shots from
 * distorting the curve, before fitSmoothCurve interpolates between them.
 */
const binCurvePoints = (
  byBin: Map<string, { value: number; timestamp: number }[]>,
  endType: QuadrantEndType,
  direction: QuadrantDirection,
  isLive: boolean
): { x: number; y: number; shots: number }[] => {
  const points: { x: number; y: number; shots: number }[] = [];
  for (let bin = BROOM_POSITION_MIN; bin < BROOM_POSITION_MAX; bin += BROOM_BIN_WIDTH_FEET) {
    const entries = byBin.get(binKeyString({ endType, bin, direction })) ?? [];
    const agg = isLive ? recentAverage(entries, RECENT_SHOT_COUNT) : average(entries);
    if (agg.avg === null) continue;
    points.push({ x: bin + BROOM_BIN_WIDTH_FEET / 2, y: agg.avg, shots: agg.used });
  }
  return points;
};

/**
 * Estimates curl for a specific broom position via a smooth curve fit
 * through this quadrant's own binned data, rather than either a single
 * side-wide average or a step function that jumps at each bin boundary:
 *
 *  1. A cubic spline through every 1ft bin's own average (recent-weighted if
 *     live) for this exact endType + direction, evaluated at the broom's
 *     exact position — dragging it a few inches shifts the estimate a few
 *     inches' worth, not a whole bin's worth.
 *  2. No shots anywhere on this side yet: the same spline built from the
 *     mirror side's bins (same distance from center, opposite side and
 *     turn — the same trajectory approached from the other side), evaluated
 *     at the mirrored position.
 *  3. Still nothing: practice shots at the tee line, or the ~4.5-5ft ideal
 *     midpoint.
 *
 * shotTypeFilter carries straight through to collectBinnedCurls: pass a shot
 * type for takeout weights (control through peel), where curl comes only
 * from other shots actually thrown at that weight, never scaled from draw
 * data; omit it for draw zones, where every shot in a bin counts.
 */
export const estimateBinnedCurl = (
  game: Game,
  practiceShots: PracticeShot[],
  endType: QuadrantEndType,
  broomPosition: number,
  direction: QuadrantDirection,
  isLive: boolean,
  shotTypeFilter?: ShotType
): QuadrantCurlEstimate => {
  const byBin = collectBinnedCurls(game, shotTypeFilter);

  const ownPoints = binCurvePoints(byBin, endType, direction, isLive);
  const ownCurve = fitSmoothCurve(ownPoints.map(p => ({ x: p.x, y: p.y })));
  if (ownCurve) {
    const shotsUsed = ownPoints.reduce((sum, p) => sum + p.shots, 0);
    return { curl: ownCurve(broomPosition), source: isLive ? 'recent' : 'average', isEstimate: false, shotsUsed };
  }

  const mirrorDirection: QuadrantDirection = direction === 'left-right' ? 'right-left' : 'left-right';
  const mirrorPoints = binCurvePoints(byBin, endType, mirrorDirection, isLive)
    .map(p => ({ x: -p.x, y: p.y })); // reflected across center — the mirror trajectory's own bins
  const mirrorCurve = fitSmoothCurve(mirrorPoints);
  if (mirrorCurve) {
    return { curl: mirrorCurve(broomPosition), source: 'mirror', isEstimate: true, shotsUsed: 0 };
  }

  const practiceAvg = practiceTeeLineCurlAverage(practiceShots);
  if (practiceAvg !== null) {
    return { curl: practiceAvg, source: 'baseline', isEstimate: true, shotsUsed: 0 };
  }

  return { curl: game.expectedCurl ?? DEFAULT_CURL_FEET, source: 'baseline', isEstimate: true, shotsUsed: 0 };
};
